import { VehicleService } from "services/vehicle/vehicle";
import getPreviousPriceAndDiscount from "./discountPricingHelper";
import getCurrentSellingPrice from "./getCurrentSellingPrice";
import regYearLetter from "./regYearLetter";
import { ResponseVehicle } from "services/motorpoint/shortlist";
import { toTitleCase, vehicleMakeFormatter } from "./language-helpers";

const generateGtmEcommerceEntryString = (
    {
        vehicle,
        itemListId,
        itemListName,
        itemListPosition,
        event,
        googlePlaceId,
    }: {
        vehicle: VehicleService.Vehicle | ResponseVehicle;
        itemListId: string;
        itemListName?: string;
        event: "view_item" | "add_to_wishlist" | "select_item" | "coming_soon_alert";
        googlePlaceId?: string;
        itemListPosition?: number;
    },
    asObject = false
): string | object => {
    const vehicleResponse = vehicle as ResponseVehicle;
    const vehicleFull = vehicle as VehicleService.Vehicle;

    const { moneyOff } = getPreviousPriceAndDiscount(vehicle, 0) ?? { moneyOff: 0 };

    const formattedMake = vehicleMakeFormatter(
        vehicleFull?.Taxonomy?.Manufacturer ?? vehicleResponse.Make
    );
    const formattedModel = vehicleMakeFormatter(
        vehicleFull?.Taxonomy?.Model ?? vehicleResponse.Model
    );
    const formattedVariant = toTitleCase(vehicleFull.Taxonomy?.Trim ?? vehicleResponse?.Trim);

    const gtmItemName = `${formattedMake} ${formattedModel}`;
    const gtmPrice = getCurrentSellingPrice(vehicle);

    const registrationDate = vehicleFull.DateOfFirstUKRegistration
        ? new Date(vehicleFull.DateOfFirstUKRegistration)
        : new Date();

    const regDate = vehicleFull.DateOfFirstUKRegistration
        ? `${registrationDate.toLocaleDateString("en-gb", {
              year: "numeric",
          })} (${regYearLetter(registrationDate)} plate)`
        : `${vehicleResponse.RegYear}`;

    const isCommercial = vehicleFull?.Taxonomy?.IsCommercial ?? vehicleResponse?.IsCommercial;
    const bodyType = vehicleFull?.BodyType ?? vehicleResponse?.BodyStyle;
    const fuelType = vehicleFull?.FuelType ?? vehicleResponse?.FuelType;
    const stockBookNumber = vehicleFull?.StockBookNumber ?? vehicleResponse?.StockBookNumber;
    const availability =
        vehicleFull.ComingSoonDetails?.IsComingSoon || vehicleResponse.IsComingSoon
            ? "coming soon"
            : "in stock";

    function GetVehicleLocationName(
        fullVehicle: VehicleService.Vehicle,
        responseVehicle: ResponseVehicle
    ) {
        if (fullVehicle.ComingSoonDetails?.IsComingSoon || responseVehicle.IsComingSoon) {
            return "Preperation";
        }
        if (fullVehicle?.CurrentLocation?.Name) {
            return fullVehicle?.CurrentLocation?.Name;
        }
        if (responseVehicle?.Location) {
            return responseVehicle?.Location;
        }
    }
    const location = GetVehicleLocationName(vehicleFull, vehicleResponse);

    const transmission = vehicleFull?.Transmission ?? vehicleResponse?.Transmission ?? "Unknown";
    const mileage = vehicleFull?.Mileage ?? vehicleResponse?.Mileage;
    const matchType = vehicleResponse?.MatchType ?? "";

    const eventObject = {
        event,
        ecommerce: {
            items: [
                {
                    item_list_id: itemListId,
                    item_brand: formattedMake,
                    item_category: isCommercial ? "Vans" : "Cars",
                    item_category2: gtmItemName,
                    item_category3: regDate,
                    item_category4: bodyType,
                    item_category5: fuelType,
                    transmission: transmission,
                    mileage: `${mileage}`,
                    item_id: `${stockBookNumber}`,
                    item_name: gtmItemName,
                    item_variant: formattedVariant,
                    affiliation: `Motorpoint ${location}`,
                    currency: "GBP",
                    discount: moneyOff,
                    location_id: googlePlaceId || "",
                    price: gtmPrice,
                    quantity: 1,
                    availability,
                    ...(matchType ? { reason: matchType } : {}),
                    ...(itemListName ? { item_list_name: itemListName } : {}),
                    ...(itemListPosition ? { index: itemListPosition } : {}),
                },
            ],
        },
    };

    return !asObject ? JSON.stringify(eventObject) : eventObject;
};

export default generateGtmEcommerceEntryString;
